import { militaryToBackgroundTimeSlot } from '../../hangout/shared/message-tree.js';
import { isPlaceholderCharacterName } from './character-name-utils.js';
import { parseSpriteState } from '../../../core/utils/hangoutUtils.js';
import { normalizeId, parseNumericId } from '../../hangout/shared/id-utils.js';

const resolveAccumulatedCharacterName = ({ incomingName, existingName, characterId }) => {
  const candidates = [incomingName, existingName];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    if (!isPlaceholderCharacterName(trimmed, characterId)) return trimmed;
  }
  return `Character ${characterId}`;
};

const getLastMessageSpriteTuple = (messages, characterId) => {
  const out = { outfitId: '0', actionId: null };
  if (!messages?.length) return out;

  const cid = String(characterId);

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    if (String(message.characterId) !== cid) continue;

    if (message.outfitId !== undefined && message.outfitId !== null) {
      out.outfitId = String(message.outfitId);
    }
    if (message.actionId != null && message.actionId !== '') {
      const parsed = Number.parseInt(String(message.actionId), 10);
      out.actionId = Number.isFinite(parsed) ? parsed : null;
    }
    return out;
  }

  return out;
};

export const accumulateStateFromMessages = (messages) => {
  const currentState = {
    loc_id: 0,
    timeOfDay: 'day',
    activeCharacters: []
  };

  const characterMap = new Map();

  for (const message of messages) {
    if (message?.characterId) {
      const characterId = String(message.characterId);
      const existing = characterMap.get(characterId) || {};
      characterMap.set(characterId, {
        id: characterId,
        name: resolveAccumulatedCharacterName({
          incomingName: message.characterName,
          existingName: existing.name,
          characterId
        }),
        outfitId: String(message.outfitId ?? existing.outfitId ?? 0),
        sprite_state: message.actionId ?? existing.sprite_state ?? null,
        goal: existing.goal || null
      });
    }

    const topLocationRaw = message.locationId ?? message.location_id;
    if (topLocationRaw !== undefined && topLocationRaw !== null && topLocationRaw !== '') {
      const parsedLocation = Number.parseInt(String(topLocationRaw), 10);
      if (Number.isFinite(parsedLocation) && parsedLocation >= 0) {
        currentState.loc_id = parsedLocation;
      }
    }

    if (message.time !== undefined && message.time !== null && message.time !== '') {
      const slot = militaryToBackgroundTimeSlot(message.time);
      if (slot) currentState.timeOfDay = slot;
    }
  }

  currentState.activeCharacters = Array.from(characterMap.values());
  return currentState;
};

const parsePreviewInteger = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLastRound = (lastRound = []) => {
  if (!Array.isArray(lastRound)) return [];
  const seen = new Set();
  const normalized = [];
  for (const entry of lastRound) {
    const characterId = String(entry?.characterId ?? entry?.character_id ?? '');
    if (!characterId || seen.has(characterId)) continue;
    seen.add(characterId);
    const actionId = parsePreviewInteger(entry?.actionId ?? entry?.action_id);
    const outfitId = parsePreviewInteger(entry?.outfitId ?? entry?.outfit_id);
    normalized.push({
      characterId,
      actionId,
      outfitId: outfitId != null ? outfitId : 0
    });
  }
  return normalized;
};

export const buildListPreviewFromMessages = (messages = []) => {
  const getCreatedAtValue = (message) => {
    const createdAt = message?.createdAt ?? message?.timestamp ?? message?.created_at ?? 0;
    if (typeof createdAt === 'string') {
      const parsed = Date.parse(createdAt);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return Number(createdAt) || 0;
  };
  const comparePreviewMessages = (left, right) => {
    const createdDelta = getCreatedAtValue(left) - getCreatedAtValue(right);
    if (createdDelta !== 0) return createdDelta;
    return String(left?.path || '').localeCompare(String(right?.path || ''), 'en', { numeric: true });
  };

  const list = [...(Array.isArray(messages) ? messages : [])].sort(comparePreviewMessages);
  let lastLocationId = null;
  let lastTime = null;
  let lastAssistantIndex = -1;

  for (let i = 0; i < list.length; i += 1) {
    const message = list[i];
    const locationId = parsePreviewInteger(message?.locationId ?? message?.location_id);
    if (locationId != null) lastLocationId = locationId;
    const time = parsePreviewInteger(message?.time);
    if (time != null) lastTime = time;
    if (message?.role === 'assistant') lastAssistantIndex = i;
  }

  if (lastAssistantIndex < 0) {
    return { lastLocationId, lastTime, lastRound: [] };
  }

  let roundStart = 0;
  for (let i = lastAssistantIndex; i >= 0; i -= 1) {
    if (list[i]?.role === 'user') {
      roundStart = i + 1;
      break;
    }
  }

  const latestByCharacter = new Map();
  for (let i = roundStart; i < list.length; i += 1) {
    const message = list[i];
    if (message?.role !== 'assistant') continue;
    const characterId = String(message?.characterId ?? message?.character_id ?? '');
    if (!characterId) continue;
    latestByCharacter.set(characterId, {
      characterId,
      actionId: parsePreviewInteger(message?.actionId ?? message?.action_id),
      outfitId: parsePreviewInteger(message?.outfitId ?? message?.outfit_id) ?? 0
    });
  }

  return {
    lastLocationId,
    lastTime,
    lastRound: Array.from(latestByCharacter.values())
  };
};

export const getLastRoundSpriteTuple = (conversation, characterId) => {
  const round = normalizeLastRound(conversation?.lastRound);
  const match = round.find((entry) => String(entry.characterId) === String(characterId));
  if (match) {
    return {
      outfitId: String(match.outfitId ?? 0),
      actionId: match.actionId
    };
  }
  return getLastMessageSpriteTuple(conversation?.messages, characterId);
};

export const lastRoundToActiveCharacters = (lastRound = []) =>
  normalizeLastRound(lastRound).map((entry) => ({
    id: String(entry.characterId),
    name: `Character ${entry.characterId}`,
    outfitId: String(entry.outfitId ?? 0),
    sprite_state: entry.actionId ?? null,
    actionId: entry.actionId ?? null
  }));

export const reconstructFinalStateFromConversation = (conversation) => {
  const previewLocation = parsePreviewInteger(conversation?.lastLocationId);
  const previewTime = parsePreviewInteger(conversation?.lastTime);
  if (previewLocation != null || previewTime != null) {
    return {
      timeOfDay: (previewTime != null ? militaryToBackgroundTimeSlot(previewTime) : null) || 'night',
      loc_id: previewLocation
    };
  }

  if (!conversation?.messages || !Array.isArray(conversation.messages)) {
    return { timeOfDay: 'night', loc_id: null };
  }

  const accumulated = accumulateStateFromMessages(conversation.messages);
  return {
    timeOfDay: accumulated.timeOfDay || 'night',
    loc_id: accumulated.loc_id || null
  };
};

const UNKNOWN_SCENARIO = 'Unknown Scenario';

export const parseConversationData = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const normalizeScenarioId = (scenarioId) => {
  const raw = normalizeId(scenarioId);
  if (!raw) return '';
  const numeric = parseNumericId(raw);
  return numeric == null ? raw : String(numeric);
};

export const resolveConversationScenarioName = ({ mappedName, detailName, conversationName }) => {
  if (mappedName) return mappedName;
  if (detailName && detailName !== UNKNOWN_SCENARIO) return detailName;
  if (conversationName && conversationName !== UNKNOWN_SCENARIO) return conversationName;
  return UNKNOWN_SCENARIO;
};

export const resolveActionFromConversationMetadata = (metadata) => {
  let actionId = null;
  let outfitId = null;
  if (metadata?.lastSpriteState || metadata?.lastEmotion) {
    const parsedState = parseSpriteState(metadata.lastSpriteState || metadata.lastEmotion);
    const parsedAction = Number(parsedState.action);
    actionId = Number.isFinite(parsedAction) ? parsedAction : (parsedState.action ?? null);
  }
  if (metadata?.outfitId !== undefined && metadata?.outfitId !== null) {
    outfitId = String(metadata.outfitId);
  }
  return { actionId, outfitId };
};
